import React, { useEffect, useState } from 'react';
import {
  Phone,
  Truck,
  BarChart3,
  Users,
  ShieldCheck,
  ShieldX,
  Lock,
  CheckCircle2,
  AlertCircle,
  Activity,
  Clock,
} from 'lucide-react';

const API_URL = '/api';

function StatCard({ label, value, icon: Icon, accent = 'brand' }) {
  const accentMap = {
    brand: 'bg-brand-50 text-brand-700',
    success: 'bg-success-50 text-success-700',
    warning: 'bg-warning-50 text-warning-700',
  };
  return (
    <div className="card p-5 flex items-center gap-4">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${accentMap[accent]}`}>
        <Icon size={20} strokeWidth={2.25} />
      </div>
      <div className="flex flex-col min-w-0">
        <span className="text-2xl font-semibold text-ink leading-none">{value}</span>
        <span className="text-xs text-ink-subtle mt-1">{label}</span>
      </div>
    </div>
  );
}

function ActionCard({ icon: Icon, title, description, onClick }) {
  return (
    <button
      onClick={onClick}
      className="card p-5 text-left transition-all duration-150 ease-snap
                 hover:shadow-pop hover:-translate-y-0.5 hover:border-brand-200
                 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40"
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-brand-50 text-brand-700 flex items-center justify-center">
          <Icon size={18} strokeWidth={2.25} />
        </div>
        <span className="text-sm font-semibold text-ink">{title}</span>
      </div>
      <p className="text-sm text-ink-subtle leading-relaxed">{description}</p>
    </button>
  );
}

function PermissionRow({ label, granted }) {
  return (
    <div className="flex items-center justify-between py-2.5 border-b border-line/60 last:border-b-0">
      <span className="text-sm text-ink">{label}</span>
      <span className={granted ? 'chip-success' : 'chip-neutral'}>
        {granted ? <ShieldCheck size={12} /> : <ShieldX size={12} />}
        {granted ? 'Liberado' : 'Bloqueado'}
      </span>
    </div>
  );
}

export default function Dashboard({
  perfilLabel,
  podeVerRelatorios,
  podeAdministrarUsuarios,
  podeOperarAtendimento,
  podeGerenciarPrestadores,
  apiFetch,
  setAbaAtiva,
}) {
  const [senhaAtual, setSenhaAtual] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [carregandoSenha, setCarregandoSenha] = useState(false);
  const [erroSenha, setErroSenha] = useState(null);
  const [mensagemSenha, setMensagemSenha] = useState(null);

  const [stats, setStats] = useState(null);
  const [carregandoStats, setCarregandoStats] = useState(true);

  useEffect(() => {
    let cancelado = false;
    (async () => {
      try {
        const response = await apiFetch(`${API_URL}/dashboard/stats`);
        if (!response.ok) throw new Error('Falha ao buscar indicadores');
        const data = await response.json();
        if (!cancelado) setStats(data);
      } catch {
        if (!cancelado) setStats(null);
      } finally {
        if (!cancelado) setCarregandoStats(false);
      }
    })();
    return () => { cancelado = true; };
  }, [apiFetch]);

  const valorStat = (chave) => {
    if (carregandoStats) return '…';
    if (!stats) return '—';
    return stats[chave];
  };

  const permissoes = [
    { label: 'Operação de atendimento', granted: podeOperarAtendimento },
    { label: 'Gestão de prestadores', granted: podeGerenciarPrestadores },
    { label: 'Relatórios', granted: podeVerRelatorios },
    { label: 'Administração de usuários', granted: podeAdministrarUsuarios },
  ];

  const trocarMinhaSenha = async (e) => {
    e.preventDefault();
    try {
      setCarregandoSenha(true);
      setErroSenha(null);
      setMensagemSenha(null);

      if (!senhaAtual || !novaSenha || !confirmarSenha) {
        throw new Error('Preencha todos os campos de senha.');
      }
      if (novaSenha !== confirmarSenha) {
        throw new Error('A confirmação da nova senha não confere.');
      }
      if (novaSenha.length < 6) {
        throw new Error('A nova senha deve ter pelo menos 6 caracteres.');
      }

      const response = await apiFetch(`${API_URL}/usuarios/me/senha`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ senhaAtual, novaSenha }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Erro ao alterar a senha.');
      }
      setMensagemSenha('Sua senha foi alterada com sucesso.');
      setSenhaAtual('');
      setNovaSenha('');
      setConfirmarSenha('');
    } catch (err) {
      setErroSenha(err.message || 'Erro ao alterar a senha.');
    } finally {
      setCarregandoSenha(false);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-ink tracking-tight">Dashboard</h1>
          <p className="text-sm text-ink-subtle mt-1">
            Visão geral do seu acesso e das principais ações da mesa de atendimento.
          </p>
        </div>
        <div className="chip-brand !px-3 !py-1.5">
          <span className="text-[11px] uppercase tracking-wider opacity-70">Perfil</span>
          <span className="font-semibold">{perfilLabel}</span>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard label="Atendimentos hoje" value={valorStat('atendimentosHoje')} icon={Activity} accent="brand" />
        <StatCard label="Em andamento" value={valorStat('emAndamento')} icon={Clock} accent="warning" />
        <StatCard label="Prestadores ativos" value={valorStat('prestadoresAtivos')} icon={Truck} accent="success" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className="card p-5">
          <header className="mb-3">
            <h2 className="text-sm font-semibold text-ink">Permissões do seu perfil</h2>
            <p className="text-xs text-ink-subtle mt-0.5">
              O que o seu perfil pode acessar neste sistema.
            </p>
          </header>
          <div>
            {permissoes.map((p) => (
              <PermissionRow key={p.label} label={p.label} granted={p.granted} />
            ))}
          </div>
        </section>

        <section>
          <header className="mb-3">
            <h2 className="text-sm font-semibold text-ink">Ações rápidas</h2>
            <p className="text-xs text-ink-subtle mt-0.5">Atalhos para as áreas mais usadas.</p>
          </header>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {podeOperarAtendimento && (
              <ActionCard
                icon={Phone}
                title="Novo Atendimento"
                description="Buscar associado, validar situação e iniciar atendimento."
                onClick={() => setAbaAtiva?.('novo')}
              />
            )}
            {podeGerenciarPrestadores && (
              <ActionCard
                icon={Truck}
                title="Prestadores"
                description="Consultar e gerenciar prestadores disponíveis."
                onClick={() => setAbaAtiva?.('prestadores')}
              />
            )}
            {podeVerRelatorios && (
              <ActionCard
                icon={BarChart3}
                title="Central"
                description="Acompanhar atendimentos e indicadores operacionais."
                onClick={() => setAbaAtiva?.('historico')}
              />
            )}
            {podeAdministrarUsuarios && (
              <ActionCard
                icon={Users}
                title="Usuários"
                description="Criar usuários, perfis e controlar acessos."
                onClick={() => setAbaAtiva?.('usuarios')}
              />
            )}
          </div>
        </section>
      </div>

      <section className="card p-5">
        <header className="mb-4 flex items-center gap-2">
          <Lock size={16} className="text-ink-muted" />
          <h2 className="text-sm font-semibold text-ink">Segurança da conta</h2>
        </header>

        {erroSenha && (
          <div className="mb-3 flex items-start gap-2 rounded-xl bg-danger-50 text-danger-700 px-3.5 py-2.5 text-sm">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>{erroSenha}</span>
          </div>
        )}
        {mensagemSenha && (
          <div className="mb-3 flex items-start gap-2 rounded-xl bg-success-50 text-success-700 px-3.5 py-2.5 text-sm">
            <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
            <span>{mensagemSenha}</span>
          </div>
        )}

        <form onSubmit={trocarMinhaSenha} className="flex flex-col gap-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="label" htmlFor="senha-atual">Senha atual</label>
              <input
                id="senha-atual"
                type="password"
                value={senhaAtual}
                onChange={(e) => setSenhaAtual(e.target.value)}
                className="input"
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="nova-senha">Nova senha</label>
              <input
                id="nova-senha"
                type="password"
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
                className="input"
                required
              />
            </div>
            <div>
              <label className="label" htmlFor="confirmar-senha">Confirmar nova senha</label>
              <input
                id="confirmar-senha"
                type="password"
                value={confirmarSenha}
                onChange={(e) => setConfirmarSenha(e.target.value)}
                className="input"
                required
              />
            </div>
          </div>
          <div>
            <button type="submit" className="btn-primary" disabled={carregandoSenha}>
              <Lock size={16} />
              {carregandoSenha ? 'Salvando…' : 'Alterar minha senha'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
